import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Play, ArrowRight, Zap, Target, TrendingUp, Sparkles, DollarSign } from 'lucide-react';
import HeroBackground from './HeroBackground';
import { Link } from 'react-router-dom';
import Partner from './Partner';
const Hero = () => {
  const heroRef = useRef(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [fallingDollars, setFallingDollars] = useState([]);
  const words = [];

  useEffect(() => {
    // Text morphing animation
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % words.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Create falling dollars animation on component mount
    const createFallingDollar = () => {
      const id = Math.random();
      const dollar = {
        id,
        left: Math.random() * 100,
        animationDelay: Math.random() * 2,
        animationDuration: 3 + Math.random() * 2,
        size: 20 + Math.random() * 15,
      };
      
      setFallingDollars(prev => [...prev, dollar]);
      
      // Remove dollar after animation completes
      setTimeout(() => {
        setFallingDollars(prev => prev.filter(d => d.id !== id));
      }, (dollar.animationDuration + dollar.animationDelay) * 1000);
    };

    // Create initial burst of dollars
    for (let i = 0; i < 15; i++) {
      setTimeout(() => createFallingDollar(), i * 200);
    }

    // Continue creating dollars periodically
    const dollarInterval = setInterval(() => {
      createFallingDollar();
    }, 800);

    return () => {
      clearInterval(dollarInterval);
    };
  }, []);

  const scrollToNext = () => {
    const nextSection = document.querySelector('.next-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
    }
  };

  return (
    <>
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        .hero {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 100px 2rem 60px;
          background: #000;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .falling-dollars {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 5;
          overflow: hidden;
        }

        .falling-dollar {
          position: absolute;
          top: -50px;
          color: #FFD700;
          animation: fall linear forwards;
          opacity: 0.7;
          filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.5));
        }

        @keyframes fall {
          from {
            transform: translateY(-50px) rotate(0deg);
            opacity: 0.7;
          }
          to {
            transform: translateY(calc(100vh + 50px)) rotate(360deg);
            opacity: 0;
          }
        }

        .hero-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 1;
        }

        .gradient-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.6;
          will-change: transform;
          animation: float 8s ease-in-out infinite;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, #FFD700 0%, #FF6B35 40%, transparent 70%);
          top: 10%;
          left: 10%;
          animation: float 8s ease-in-out infinite;
        }

        .orb-2 {
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, #00D9FF 0%, #9333EA 40%, transparent 70%);
          top: 60%;
          right: 10%;
          animation: float 10s ease-in-out infinite reverse;
        }

        .orb-3 {
          width: 250px;
          height: 250px;
          background: radial-gradient(circle, #9333EA 0%, #FFD700 40%, transparent 70%);
          bottom: 20%;
          left: 30%;
          animation: float 12s ease-in-out infinite;
        }

        .grid-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: gridMove 20s linear infinite;
        }

        .noise-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><defs><filter id="noiseFilter"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="1" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/></filter></defs><rect width="100%" height="100%" filter="url(%23noiseFilter)" opacity="0.03"/></svg>');
        }

        .hero-content {
          position: relative;
          z-index: 10;
          text-align: center;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          animation: fadeInUp 1s ease-out;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          position: relative;
          overflow: hidden;
          animation: slideInDown 0.8s ease-out 0.2s both;
        }

        .hero-badge::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          animation: shimmer 3s infinite;
        }

        .badge-icon {
          width: 16px;
          height: 16px;
          color: #FFD700;
          filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5));
        }

        .sparkle-icon {
          width: 14px;
          height: 14px;
          color: #00D9FF;
          animation: sparkle 2s ease-in-out infinite;
        }

        .hero-title {
          font-size: clamp(2.5rem, 8vw, 6rem);
          font-weight: 900;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          color: #fff;
        }

        .title-line {
          display: block;
          margin-bottom: 0.25rem;
        }

        .word {
          display: inline-block;
          margin-right: 1rem;
          background: linear-gradient(135deg, #fff 0%, #fff 30%, #FFD700 40%, #00D9FF 60%, #9333EA 70%, #fff 100%);
          background-size: 400% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          animation: shimmerText 4s ease-in-out infinite, slideInUp 0.8s ease-out both;
          position: relative;
        }

        .word:nth-child(1) { animation-delay: 0.3s; }
        .word:nth-child(2) { animation-delay: 0.4s; }
        .word:nth-child(3) { animation-delay: 0.5s; }

        .word.highlight {
          background: linear-gradient(135deg, #FFD700 0%, #FFC947 40%, #fff3d6 100%);
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          font-weight: 1000;
          animation: gradientShift 2s ease-in-out infinite, breathe 2s ease-in-out infinite, slideInUp 0.8s ease-out both;
          filter: drop-shadow(0 0 20px rgba(255, 215, 0, 0.3));
        }

        .hero-subtitle {
          font-size: clamp(1.1rem, 2vw, 1.3rem);
          color: rgba(255, 255, 255, 0.8);
          font-weight: 400;
          line-height: 1.6;
          margin-bottom: 2.5rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          animation: slideInUp 0.8s ease-out 0.6s both;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-bottom: 2.5rem;
        }

        .stat {
          text-align: center;
          position: relative;
          padding: 0.75rem 1rem;
          border-radius: 1rem;
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.3s ease;
          min-width: 120px;
          animation: slideInUp 0.6s ease-out both;
          animation-delay: calc(var(--delay) * 0.1s);
        }

        .stat::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(0, 217, 255, 0.1));
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .stat:hover {
          transform: translateY(-5px) rotateY(5deg);
          box-shadow: 0 20px 40px rgba(255, 215, 0, 0.2);
        }

        .stat:hover::before {
          opacity: 1;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 900;
          background: linear-gradient(135deg, #FFD700, #00D9FF);
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          line-height: 1;
          margin-bottom: 0.5rem;
          position: relative;
          z-index: 1;
        }

        .stat-label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
          position: relative;
          z-index: 1;
        }

        .hero-cta {
          display: flex;
          gap: 1.25rem;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          margin-top: 2rem;
        }

        .cta-primary,
        .cta-secondary {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.75rem;
          border-radius: 50px;
          font-size: 1.1rem;
          font-weight: 600;
          text-decoration: none;
          border: none;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          position: relative;
          overflow: hidden;
          min-width: 180px;
          justify-content: center;
          animation: slideInUp 0.6s ease-out both;
        }

        .cta-primary {
          background: linear-gradient(135deg, #FFD700, #00D9FF);
          color: #000;
          box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
          animation-delay: 0.8s;
        }

        .cta-primary:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
        }

        .cta-secondary {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          box-shadow: 0 10px 30px rgba(0, 217, 255, 0.2);
          animation-delay: 0.9s;
        }

        .cta-secondary:hover {
          transform: translateY(-3px) scale(1.02);
          background: rgba(255, 255, 255, 0.1);
          box-shadow: 0 15px 40px rgba(0, 217, 255, 0.3);
        }

        .cta-primary::before,
        .cta-secondary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .cta-primary:hover::before,
        .cta-secondary:hover::before {
          left: 100%;
        }

        .scroll-indicator {
          position: absolute;
          bottom: 1.5rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          transition: all 0.3s ease;
          animation: bounce 2s ease-in-out infinite;
        }

        .scroll-indicator:hover {
          color: #fff;
          transform: translateX(-50%) translateY(-5px);
        }

        .scroll-icon {
          width: 24px;
          height: 24px;
          filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
        }

        .scroll-indicator span {
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* Animations */
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) rotate(0deg); 
          }
          33% { 
            transform: translate(30px, -30px) rotate(120deg); 
          }
          66% { 
            transform: translate(-20px, 20px) rotate(240deg); 
          }
        }

        @keyframes gridMove {
          0% { 
            background-position: 0 0; 
          }
          100% { 
            background-position: 50px 50px; 
          }
        }

        @keyframes shimmer {
          0% { 
            left: -100%; 
          }
          100% { 
            left: 100%; 
          }
        }

        @keyframes sparkle {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
            opacity: 1; 
          }
          50% { 
            transform: scale(1.2) rotate(180deg); 
            opacity: 0.7; 
          }
        }

        @keyframes shimmerText {
          0% { 
            background-position: -400% 0; 
          }
          100% { 
            background-position: 400% 0; 
          }
        }

        @keyframes gradientShift {
          0%, 100% { 
            background-position: 0% 50%; 
          }
          50% { 
            background-position: 100% 50%; 
          }
        }

        @keyframes breathe {
          0%, 100% { 
            transform: scale(1); 
          }
          50% { 
            transform: scale(1.02); 
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { 
            transform: translateX(-50%) translateY(0); 
          }
          40% { 
            transform: translateX(-50%) translateY(-10px); 
          }
          60% { 
            transform: translateX(-50%) translateY(-5px); 
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero {
            padding: 80px 1rem 40px;
          }
          
          .hero-content {
            padding: 0 0.5rem;
          }
          
          .hero-stats {
            gap: 1.5rem;
            flex-wrap: wrap;
          }
          
          .stat {
            min-width: 100px;
            padding: 0.5rem 0.75rem;
          }
          
          .stat-number {
            font-size: 2rem;
          }
          
          .hero-cta {
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .cta-primary, .cta-secondary {
            width: 100%;
            max-width: 260px;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
          }

          .falling-dollar {
            animation-duration: 4s !important;
          }
        }

        @media (max-width: 480px) {
          .hero-stats {
            flex-direction: column;
            gap: 1rem;
            align-items: center;
          }
          
          .stat {
            width: 100%;
            max-width: 200px;
          }
          
          .hero-badge {
            font-size: 0.8rem;
            padding: 0.375rem 0.875rem;
          }

          .cta-primary, .cta-secondary {
            padding: 0.75rem 1.25rem;
            min-width: 160px;
          }
        }
      `}</style>

      <section ref={heroRef} className="hero">
        <HeroBackground/>
        {/* Falling Dollars Animation */}
        <div className="falling-dollars">
          {fallingDollars.map((dollar) => (
            <DollarSign
              key={dollar.id}
              className="falling-dollar"
              size={dollar.size}
              style={{
                left: `${dollar.left}%`,
                animationDelay: `${dollar.animationDelay}s`,
                animationDuration: `${dollar.animationDuration}s`,
              }}
            />
          ))}
        </div>

        <div className="hero-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-overlay"></div>
          <div className="noise-overlay"></div>
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <Target className="badge-icon" />
            <span>Stop Guessing, Start Growing</span>
            <Sparkles className="sparkle-icon" />
          </div>

          <h1 className="hero-title">
            <span className="title-line">
              <span className="word">Turn</span>
           
              <span className="word">Wasted Spend</span>
            </span>
            <span className="title-line">
              <span className="word highlight">into</span>
              <span className="word">Growth.</span>
            </span>
          </h1>

          <p className="hero-subtitle">
        We optimize your funnel to reduce wasted spend and drive profitable business growth at scale.
          </p>

          <div className="hero-stats">
            <div className="stat" style={{ '--delay': 1 }}>
              <div className="stat-number">$50M</div>
              <div className="stat-label">Yearly Ad Spend</div>
            </div>
            <div className="stat" style={{ '--delay': 2 }}>
              <div className="stat-number">150+</div>
              <div className="stat-label">Happy Clients</div>
            </div>
            <div className="stat" style={{ '--delay': 3 }}>
              <div className="stat-number">5+</div>
              <div className="stat-label">Years in Market</div>
            </div>
          </div>

          <div className="hero-cta">
            <Link to="/consultation"> 
            <button className="cta-primary" onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}>
              <Zap size={18} />
              Scale Your Business Today 
            </button>
            </Link>
            <button className="cta-secondary" onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}>
              <Play size={18} />
              Watch Demo
            </button>
          </div>
        </div>
     

         {/* Scroll Indicator */}

        <div className="scroll-indicator" onClick={scrollToNext}>
          <ChevronDown className="scroll-icon" />
          <span>Scroll to explore</span>
        </div>
          
      </section>
       <Partner />
    </>
  );
};

export default Hero;